import type {
  GpmfGpsSample,
  GpmfPayload,
  GpmfSampleTiming,
  GpmfStreams,
  GpmfXyzSample,
} from './GpmfTypes'
import { US_PER_MS } from '../../utils/timeUnits'
import { gps9TimeMs } from './gps9Time'
import { minOf } from '../../utils/extremes'

/**
 * The GPMF KLV reader.
 *
 * GPMF is a key-length-value stream: a four-character key, a type byte, a
 * struct size, a repeat count, then that many structs, padded to a 4-byte
 * boundary. Containers (type 0) nest, so the reader is a recursive descent and
 * unknown keys are skipped rather than fatal.
 */

interface SampleWindow {
  startMs: number
  durationMs: number
}

/**
 * Timestamp for sample `index` of `count` within a payload.
 *
 * A GPMF payload carries a block of readings covering its own media window, so
 * a sample's time is its position inside that block. Deriving it from the
 * sample's index in the whole session instead requires assuming a rate: the
 * previous code assumed 1 ms per accelerometer sample (1000 Hz) against a real
 * ~200 Hz, so the sensor clock ran out five times faster than the recording and
 * every later reading merged against the last sample the stream had.
 *
 * `fallbackMs` stands when there is no container timing to read (raw KLV).
 */
function sampleCts(
  window: SampleWindow | null,
  index: number,
  count: number,
  fallbackMs: number
): number {
  if (!window || count <= 0) return fallbackMs
  return window.startMs + (index * window.durationMs) / count
}

/**
 * Timing for sample `index` of `count` within a payload, from that payload's
 * STMP — undefined when the container carried no STMP to read.
 */
function sampleTiming(
  meta: StreamMeta,
  index: number,
  count: number
): GpmfSampleTiming | undefined {
  if (meta.stmpUs === undefined) return undefined
  return { stmpUs: meta.stmpUs, index, count }
}

/**
 * Read the first value of a single-number payload (GPSF, GPSP), honouring the
 * declared type rather than assuming a width.
 */
function readScalarNumber(
  data: Uint8Array,
  type: number,
  payloadOffset: number,
  payloadSize: number
): number | undefined {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  const typeChar = String.fromCharCode(type)
  try {
    if (typeChar === 'L' && payloadSize >= 4)
      return view.getUint32(payloadOffset, false)
    if (typeChar === 'l' && payloadSize >= 4)
      return view.getInt32(payloadOffset, false)
    if (typeChar === 'S' && payloadSize >= 2)
      return view.getUint16(payloadOffset, false)
    if (typeChar === 's' && payloadSize >= 2)
      return view.getInt16(payloadOffset, false)
    if (typeChar === 'B' && payloadSize >= 1) return data[payloadOffset]
  } catch {
    return undefined
  }
  return undefined
}

/** Read a 'c' (ASCII) payload, dropping the optional NULL terminator. */
function readAscii(
  data: Uint8Array,
  payloadOffset: number,
  payloadSize: number
): string {
  let text = ''
  for (let i = 0; i < payloadSize; i++) {
    const byte = data[payloadOffset + i]
    if (byte === 0) break
    text += String.fromCharCode(byte)
  }
  return text
}

/**
 * Read the values of a SCAL entry — the per-stream divisor that converts raw
 * sensor integers into physical units. Cameras vary the storage type, so the
 * type code selects how to read each value.
 */
function readScal(
  data: Uint8Array,
  type: number,
  structSize: number,
  payloadOffset: number,
  payloadSize: number
): number[] {
  const view = new DataView(
    data.buffer,
    data.byteOffset + payloadOffset,
    payloadSize
  )
  const values: number[] = []
  for (let base = 0; base + structSize <= payloadSize; base += structSize) {
    switch (String.fromCharCode(type)) {
      case 'f':
        values.push(view.getFloat32(base, false))
        break
      case 'd':
        values.push(view.getFloat64(base, false))
        break
      case 'S':
        values.push(view.getUint16(base, false))
        break
      case 'L':
        values.push(view.getUint32(base, false))
        break
      case 'l':
        values.push(view.getInt32(base, false))
        break
      default:
        // 's' (int16) and anything else sized like it
        values.push(
          structSize >= 4
            ? view.getInt32(base, false)
            : view.getInt16(base, false)
        )
    }
  }
  return values.filter((v) => v !== 0)
}

/**
 * Recursive KLV parser for the GPMF binary format.
 *
 * Matches streams by FourCC key name, not type code, because GoPro firmware
 * versions vary in which GPMF type character they emit (e.g. 'l' vs 'L' for
 * GPS5, 's' vs 'f' for ACCL/GYRO). The struct size is verified to ensure
 * the per-sample byte layout is what we expect.
 *
 * `stickyScales` carries the last SCAL seen per stream across payloads:
 * GPMF treats SCAL as sticky metadata, so a payload may omit it and rely on
 * the previously transmitted value.
 */
/**
 * Put a sensor stream on the GPS stream's clock when there is no container
 * timing to read.
 *
 * The sensor rate is a property of the recording, not something a bare KLV
 * buffer states, so assuming one is how the 1 ms-per-sample bug arose. GPS is
 * the clock everything downstream merges against, so spread the readings
 * evenly across the span GPS itself covers: the rate stays unknown, but the
 * two streams cannot drift apart.
 */
function anchorSensorStreamsToGps(
  gpsData: GpmfGpsSample[],
  samples: { cts: number; date: string }[],
  baseDate: Date
): void {
  if (gpsData.length < 2 || samples.length < 2) return
  const spanMs = gpsData[gpsData.length - 1].cts - gpsData[0].cts
  if (spanMs <= 0) return
  const step = spanMs / (samples.length - 1)
  for (let i = 0; i < samples.length; i++) {
    samples[i].cts = gpsData[0].cts + i * step
    samples[i].date = new Date(
      baseDate.getTime() + samples[i].cts
    ).toISOString()
  }
}

interface StmpGroup {
  stmpUs: number
  start: number
  end: number
  /** The payload slot of each sample from `start`, counting slots whose sample was dropped. */
  slots: number[]
  /** Slots the payload declared, dropped ones included. */
  totalSlots: number
}

/**
 * Group consecutive samples by their payload's STMP, merging adjacent groups
 * that share one (the split first-second payload) and numbering each sample's
 * slot within the merged payload.
 *
 * Returns null when the stream cannot be timed from STMP: a sample without
 * timing, or an STMP no later than the group before it. The whole stream is
 * then left on the timing it was parsed with.
 */
function groupByStmp(
  samples: Array<{ timing?: GpmfSampleTiming }>
): StmpGroup[] | null {
  const groups: StmpGroup[] = []
  let slotBase = 0
  let previousIndex = -1
  for (let i = 0; i < samples.length; i++) {
    const timing = samples[i].timing
    if (!timing) return null
    let group = groups[groups.length - 1]
    if (group && group.stmpUs === timing.stmpUs) {
      if (timing.index <= previousIndex) slotBase = group.totalSlots
    } else {
      if (group && timing.stmpUs <= group.stmpUs) return null
      slotBase = 0
      group = {
        stmpUs: timing.stmpUs,
        start: i,
        end: i,
        slots: [],
        totalSlots: 0,
      }
      groups.push(group)
    }
    group.end = i + 1
    group.slots.push(slotBase + timing.index)
    group.totalSlots = slotBase + timing.count
    previousIndex = timing.index
  }
  return groups
}

/** The span given to a stream's only STMP group, which has no neighbour to measure against. */
const DEFAULT_PAYLOAD_SPAN_US = 1_000_000

/** Average per-slot STMP interval over every group up to (not including) the last. */
function meanSamplePeriodUs(groups: StmpGroup[]): number {
  const slotsBeforeLast = groups
    .slice(0, -1)
    .reduce((total, group) => total + group.totalSlots, 0)
  return (groups[groups.length - 1].stmpUs - groups[0].stmpUs) / slotsBeforeLast
}

/**
 * Rewrite `cts` (and, for a non-satellite date, `date`) for every sample in
 * `samples` from its STMP group, measured against `originUs`.
 *
 * A group covers its own slots at the stream's mean rate, or the interval to
 * the next STMP when that is shorter: a payload whose samples were all dropped
 * would otherwise stretch the payload before it across the gap it left.
 */
function groupSpansUs(groups: StmpGroup[]): number[] {
  if (groups.length === 1) return [DEFAULT_PAYLOAD_SPAN_US]
  const meanPeriodUs = meanSamplePeriodUs(groups)
  return groups.map((group, k) => {
    const next = groups[k + 1]
    return Math.min(
      next ? next.stmpUs - group.stmpUs : Infinity,
      group.totalSlots * meanPeriodUs
    )
  })
}

function retimeStream(
  samples: Array<{
    cts: number
    date: string
    timing?: GpmfSampleTiming
    dateSource?: 'satellite' | 'clock'
  }>,
  groups: StmpGroup[],
  originUs: number,
  baseDate: Date
): void {
  const spans = groupSpansUs(groups)
  for (let k = 0; k < groups.length; k++) {
    const group = groups[k]
    const spanUs = spans[k]
    for (let i = group.start; i < group.end; i++) {
      const cts =
        (group.stmpUs - originUs) / US_PER_MS +
        (group.slots[i - group.start] * (spanUs / US_PER_MS)) / group.totalSlots
      samples[i].cts = cts
      if (samples[i].dateSource !== 'satellite') {
        samples[i].date = new Date(baseDate.getTime() + cts).toISOString()
      }
    }
  }
}

/** Which streams `applyStmpTiming` put on the camera clock. */
interface RetimedStreams {
  gps: boolean
  accl: boolean
  gyro: boolean
}

/**
 * Put every stream that can be on the camera's STMP clock. A stream is
 * re-timed only when all of its samples carry STMP timing and those timestamps
 * advance; the others keep the timing they were parsed with.
 */
function applyStmpTiming(streams: GpmfStreams, baseDate: Date): RetimedStreams {
  const gps = groupByStmp(streams.gpsData)
  const accl = groupByStmp(streams.acclData)
  const gyro = groupByStmp(streams.gyroData)
  const firstStmps = [gps, accl, gyro]
    .filter((g): g is StmpGroup[] => g !== null && g.length > 0)
    .map((g) => g[0].stmpUs)
  if (firstStmps.length === 0) return { gps: false, accl: false, gyro: false }
  const originUs = minOf(firstStmps)
  const retime = (
    samples: Parameters<typeof retimeStream>[0],
    groups: StmpGroup[] | null
  ): boolean => {
    if (!groups || groups.length === 0) return false
    retimeStream(samples, groups, originUs, baseDate)
    return true
  }
  return {
    gps: retime(streams.gpsData, gps),
    accl: retime(streams.acclData, accl),
    gyro: retime(streams.gyroData, gyro),
  }
}

/**
 * Per-stream sticky metadata. GPMF only retransmits these when they change, so
 * a payload may carry samples and rely on a value sent earlier.
 */
interface StreamMeta {
  /** SCAL — the divisor turning raw integers into physical units. */
  scal?: number[]
  /** ORIN — what each stored field is, in order. Lowercase means negated. */
  orin?: string
  /** ORIO — the order the consumer asked for. */
  orio?: string
  /** GPSF — 0 no lock, 2 a 2D fix, 3 a 3D fix. Sent alongside GPS5 at 1 Hz. */
  gpsFix?: number
  /** GPSP — dilution of precision × 100; under 500 is a good fix. */
  gpsPrecision?: number
  /** STMP — microsecond timestamp of the container's first sample on the camera clock. */
  stmpUs?: number
  /** TSMP — samples of this stream written so far, including this container. */
  tsmp?: number
}

/**
 * The weakest GPS fix whose coordinates are worth keeping.
 *
 * A GPS5 stream reports its lock separately from its samples, so without this
 * the coordinates a camera emits before it has a lock — which are not
 * plausible-but-poor, they are garbage — enter the session as real positions.
 * GPS9 carries the same field per sample and is gated on the same value.
 */
const MIN_USABLE_GPS_FIX = 2

/**
 * The field order to assume when a stream does not declare ORIN.
 *
 * This is what the parser hardcoded before it read ORIN at all, and it is the
 * HERO5/Session order from the GPMF README. Note the README also lists HERO6+
 * as storing "Y,-X,Z", which a real HERO13 contradicts: it declares ORIN "ZXY"
 * in the file. Where the two disagree the file wins, so the table is not used
 * to pick a default per camera generation.
 */
const DEFAULT_ORIN = 'ZXY'

/** The axis order a consumer gets when a stream does not declare ORIO. */
const DEFAULT_ORIO = 'XYZ'

/**
 * Resolve which stored field feeds each output axis, and with what sign.
 *
 * ORIN names the fields as stored; ORIO names the order wanted. A letter
 * matching in case is passed through, a case difference means negate — that is
 * how GPMF writes a flipped axis (the README's "Y,-X,Z" is ORIN "Yxz").
 *
 * Returns null when the two cannot be reconciled — an axis named in ORIO with
 * no field to fill it — rather than silently dropping a component.
 */
function resolveAxisMap(
  orin: string,
  orio: string
): { index: number; sign: number }[] | null {
  const map: { index: number; sign: number }[] = []
  for (const wanted of orio) {
    const index = orin.toUpperCase().indexOf(wanted.toUpperCase())
    if (index < 0) return null
    const sameCase = orin[index] === wanted
    map.push({ index, sign: sameCase ? 1 : -1 })
  }
  return map
}

/**
 * Collect a container's own metadata before reading any of its samples.
 *
 * GPMF puts metadata before the data it describes, but nothing in the format
 * requires it, and a stream whose ORIN arrived after its ACCL would have been
 * read on the default axis order for that payload and silently corrected on
 * the next one. A cheap pre-pass removes the ordering assumption.
 */
function collectContainerMeta(data: Uint8Array): StreamMeta {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  const meta: StreamMeta = {}
  let offset = 0
  while (offset + 8 <= data.byteLength) {
    const key = String.fromCharCode(
      data[offset],
      data[offset + 1],
      data[offset + 2],
      data[offset + 3]
    )
    const type = data[offset + 4]
    const structSize = data[offset + 5]
    const repeatCount = view.getUint16(offset + 6, false)
    const payloadSize = structSize * repeatCount
    const payloadOffset = offset + 8
    if (payloadOffset + payloadSize > data.byteLength) break

    if (key === 'SCAL') {
      meta.scal =
        readScal(data, type, structSize, payloadOffset, payloadSize) ??
        undefined
    } else if (key === 'ORIN') {
      meta.orin = readAscii(data, payloadOffset, payloadSize)
    } else if (key === 'ORIO') {
      meta.orio = readAscii(data, payloadOffset, payloadSize)
    } else if (key === 'GPSF') {
      meta.gpsFix = readScalarNumber(data, type, payloadOffset, payloadSize)
    } else if (key === 'GPSP') {
      meta.gpsPrecision = readScalarNumber(
        data,
        type,
        payloadOffset,
        payloadSize
      )
    } else if (key === 'STMP' && type === 0x4a && payloadSize >= 8) {
      meta.stmpUs = Number(view.getBigUint64(payloadOffset, false))
    } else if (key === 'TSMP' && type === 0x4c && payloadSize >= 4) {
      meta.tsmp = view.getUint32(payloadOffset, false)
    }

    offset = payloadOffset + ((payloadSize + 3) & ~3)
  }
  return meta
}

/**
 * The metadata a stream's samples in this container are read with: the sticky
 * values it inherited, overlaid with what the container itself declared.
 *
 * STMP and TSMP describe the container they arrive in, so a container that
 * omits them has none rather than the previous container's.
 */
function containerScopedMeta(
  key: string,
  sticky: Map<string, StreamMeta>,
  containerMeta: StreamMeta
): StreamMeta {
  const meta: StreamMeta = { ...sticky.get(key), ...containerMeta }
  if (containerMeta.stmpUs === undefined) delete meta.stmpUs
  if (containerMeta.tsmp === undefined) delete meta.tsmp
  sticky.set(key, meta)
  return meta
}

function parseGpmfKlv(
  data: Uint8Array,
  gpsData: GpmfGpsSample[],
  acclData: GpmfXyzSample[],
  gyroData: GpmfXyzSample[],
  baseDate: Date,
  sticky: Map<string, StreamMeta> = new Map(),
  window: SampleWindow | null = null
): void {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  let offset = 0
  // Applies to this container's data entries only; anything a later payload
  // omits comes back from the sticky map instead.
  const containerMeta = collectContainerMeta(data)

  while (offset + 8 <= data.byteLength) {
    const key = String.fromCharCode(
      data[offset],
      data[offset + 1],
      data[offset + 2],
      data[offset + 3]
    )
    const type = data[offset + 4]
    const structSize = data[offset + 5]
    const repeatCount = view.getUint16(offset + 6, false)
    const payloadSize = structSize * repeatCount
    const paddedSize = (payloadSize + 3) & ~3
    const payloadOffset = offset + 8

    if (payloadOffset + payloadSize > data.byteLength) break

    if (type === 0) {
      // Container (DEVC, STRM, etc.) — recurse into nested KLV entries
      const nested = data.subarray(payloadOffset, payloadOffset + paddedSize)
      parseGpmfKlv(
        nested,
        gpsData,
        acclData,
        gyroData,
        baseDate,
        sticky,
        window
      )
    } else if (key === 'GPS5' && structSize === 20) {
      // 5 × int32 per sample: lat, lon, alt, speed2d, speed3d
      const meta = containerScopedMeta(key, sticky, containerMeta)
      parseGps(
        data,
        payloadOffset,
        payloadSize,
        repeatCount,
        gpsData,
        baseDate,
        meta,
        window
      )
    } else if (key === 'GPS9' && structSize === 32) {
      // Complex type "lllllllSS" (32 bytes): lat, lon, alt, speed2d, speed3d,
      // days, secs (int32 each), then DOP, fix (uint16 each). Used by cameras
      // without a GPS5 stream (Hero 13+).
      const meta = containerScopedMeta(key, sticky, containerMeta)
      parseGps9(
        data,
        payloadOffset,
        payloadSize,
        repeatCount,
        gpsData,
        meta,
        window
      )
    } else if ((key === 'ACCL' || key === 'GYRO') && structSize === 6) {
      // 3 × int16 per sample of acceleration / angular velocity. Which field
      // is which axis is ORIN's to say, not ours to assume.
      const meta = containerScopedMeta(key, sticky, containerMeta)
      parseXyz16(
        data,
        payloadOffset,
        payloadSize,
        repeatCount,
        key === 'ACCL' ? acclData : gyroData,
        baseDate,
        meta,
        window
      )
    }

    offset = payloadOffset + paddedSize
  }
}

/**
 * Parse GPS5 data: 5 × int32 per sample
 * (lat×1e7, lon×1e7, alt×1e3, speed2d×1e3, speed3d×1e3).
 */
function parseGps(
  data: Uint8Array,
  payloadOffset: number,
  payloadSize: number,
  repeatCount: number,
  gpsData: GpmfGpsSample[],
  baseDate: Date,
  meta: StreamMeta,
  window: SampleWindow | null = null
): void {
  // Only gate on a lock the file actually reports. Pre-GPSF recordings, and
  // any stream that omits it, keep every sample rather than lose the lot to an
  // assumed default.
  if (meta.gpsFix !== undefined && meta.gpsFix < MIN_USABLE_GPS_FIX) return
  const view = new DataView(
    data.buffer,
    data.byteOffset + payloadOffset,
    payloadSize
  )
  for (let i = 0; i < repeatCount; i++) {
    const base = i * 20
    if (base + 20 > payloadSize) break
    // Without container timing the nominal 10 Hz GPS rate stands in.
    const cts = sampleCts(window, i, repeatCount, gpsData.length * 100)
    gpsData.push({
      cts,
      date: new Date(baseDate.getTime() + cts).toISOString(),
      timing: sampleTiming(meta, i, repeatCount),
      dateSource: 'clock',
      latitude: view.getInt32(base, false) / 1e7,
      longitude: view.getInt32(base + 4, false) / 1e7,
      altitude: view.getInt32(base + 8, false) / 1e3,
      speed2d: view.getInt32(base + 12, false) / 1e3,
      speed3d: view.getInt32(base + 16, false) / 1e3,
      fix: meta.gpsFix,
      precision: meta.gpsPrecision,
    })
  }
}

/**
 * Parse GPS9 data (32-byte complex samples, TYPE "lllllllSS"):
 * lat×1e7, lon×1e7, alt×1e3, speed2d×1e3, speed3d×1e2, days since 2000,
 * secs×1e3 (int32 each), then DOP×1e2, fix (uint16 each).
 * Note speed3d is scaled ×100 here, unlike ×1000 in GPS5.
 * Samples without a GPS lock (fix < 2) carry garbage coordinates and are skipped.
 */
function parseGps9(
  data: Uint8Array,
  payloadOffset: number,
  payloadSize: number,
  repeatCount: number,
  gpsData: GpmfGpsSample[],
  meta: StreamMeta,
  window: SampleWindow | null = null
): void {
  const view = new DataView(
    data.buffer,
    data.byteOffset + payloadOffset,
    payloadSize
  )
  for (let i = 0; i < repeatCount; i++) {
    const base = i * 32
    if (base + 32 > payloadSize) break
    const fix = view.getUint16(base + 30, false)
    if (fix < MIN_USABLE_GPS_FIX) continue
    // Position in the payload, so dropped no-fix samples leave their slot in
    // the timeline rather than pulling every later reading earlier.
    const cts = sampleCts(window, i, repeatCount, gpsData.length * 100)
    // Satellite time. This is
    // true UTC — unlike the camera clock (media-list cre / baseDate), which
    // is local wall time and drifts.
    const days = view.getInt32(base + 20, false)
    const msOfDay = view.getInt32(base + 24, false)
    const gpsTime = gps9TimeMs(days, msOfDay)
    gpsData.push({
      cts,
      date: new Date(gpsTime).toISOString(),
      timing: sampleTiming(meta, i, repeatCount),
      dateSource: 'satellite',
      latitude: view.getInt32(base, false) / 1e7,
      longitude: view.getInt32(base + 4, false) / 1e7,
      altitude: view.getInt32(base + 8, false) / 1e3,
      speed2d: view.getInt32(base + 12, false) / 1e3,
      speed3d: view.getInt32(base + 16, false) / 100,
      fix,
      // DOP is stored ×100, the same scale GPS5 reports GPSP on, so both
      // generations expose precision in one unit.
      precision: view.getUint16(base + 28, false),
    })
  }
}

/**
 * Parse 3 × int16 per sample — shared by ACCL and GYRO streams.
 *
 * Raw integers are divided by the stream's SCAL divisor (e.g. 417 for ACCL
 * and 939 for GYRO on a Hero 13); the value differs per camera and stream, so
 * it must come from the file rather than a constant. A single SCAL value
 * applies to every axis, otherwise one value per axis in sample order (z,x,y).
 * Without SCAL the raw values are emitted unscaled.
 */
function parseXyz16(
  data: Uint8Array,
  payloadOffset: number,
  payloadSize: number,
  repeatCount: number,
  samples: {
    cts: number
    date: string
    timing?: GpmfSampleTiming
    x: number
    y: number
    z: number
  }[],
  baseDate: Date,
  meta: StreamMeta,
  window: SampleWindow | null = null
): void {
  const scale = meta.scal ?? null
  const divisor = (field: number): number => {
    if (!scale || scale.length === 0) return 1
    return scale.length === 1 ? scale[0] : scale[field] ?? scale[0]
  }
  // SCAL indexes the stored fields, so it is applied before the relabelling.
  const axisMap = resolveAxisMap(
    meta.orin ?? DEFAULT_ORIN,
    meta.orio ?? DEFAULT_ORIO
  )
  // An ORIN/ORIO pair that cannot be reconciled would silently drop a
  // component, so fall back to the documented default rather than emit a
  // half-populated reading.
  const [toX, toY, toZ] = axisMap ?? resolveAxisMap(DEFAULT_ORIN, DEFAULT_ORIO)!
  const view = new DataView(
    data.buffer,
    data.byteOffset + payloadOffset,
    payloadSize
  )
  for (let i = 0; i < repeatCount; i++) {
    const base = i * 6
    if (base + 6 > payloadSize) break
    const cts = sampleCts(window, i, repeatCount, samples.length)
    const field = (n: number): number =>
      view.getInt16(base + n * 2, false) / divisor(n)
    samples.push({
      cts,
      date: new Date(baseDate.getTime() + cts).toISOString(),
      timing: sampleTiming(meta, i, repeatCount),
      x: toX.sign * field(toX.index),
      y: toY.sign * field(toY.index),
      z: toZ.sign * field(toZ.index),
    })
  }
}

/**
 * Parse payloads whose media timing the container gave us — the accurate path,
 * and the one every real recording takes.
 */
export function parseGpmfPayloads(
  payloads: GpmfPayload[],
  baseDate: Date
): GpmfStreams {
  const streams: GpmfStreams = { gpsData: [], acclData: [], gyroData: [] }
  // SCAL is sticky across payloads, so one map spans the whole stream.
  const sticky = new Map<string, StreamMeta>()
  for (const payload of payloads) {
    parseGpmfKlv(
      payload.bytes,
      streams.gpsData,
      streams.acclData,
      streams.gyroData,
      baseDate,
      sticky,
      { startMs: payload.startMs, durationMs: payload.durationMs }
    )
  }
  applyStmpTiming(streams, baseDate)
  return streams
}

/**
 * Parse a bare KLV buffer, with no container to state when anything happened.
 *
 * The sample rate is a property of the recording rather than something the
 * bytes declare, so nothing here can recover it. A stream the camera stamped
 * with STMP carries its own clock; the rest are put on the GPS clock instead:
 * the rate stays unknown, but they cannot drift apart.
 */
export function parseGpmfBuffer(
  bytes: Uint8Array,
  baseDate: Date
): GpmfStreams {
  const streams: GpmfStreams = { gpsData: [], acclData: [], gyroData: [] }
  parseGpmfKlv(
    bytes,
    streams.gpsData,
    streams.acclData,
    streams.gyroData,
    baseDate
  )
  const retimed = applyStmpTiming(streams, baseDate)
  if (!retimed.accl)
    anchorSensorStreamsToGps(streams.gpsData, streams.acclData, baseDate)
  if (!retimed.gyro)
    anchorSensorStreamsToGps(streams.gpsData, streams.gyroData, baseDate)
  return streams
}
